import { LoadDepartments } from 'src/app/department/state/department.actions';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  StateToken,
  State,
  Action,
  StateContext,
  Selector,
  Actions,
  ofActionSuccessful,
} from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { ToastService } from 'src/app/core/services/toast.service';
import { UserOptionsApiService } from 'src/app/core/services/user-options-api.service';
import {
  IUserAccess,
  ITenantUserAccess,
  IDepartmentInfo,
  IModulePrivileges,
  IUserRole,
} from 'src/app/shared/user-view/models/tenant-user-access.model';
import { patch } from '@ngxs/store/operators';
import { LoadDueListCounts, LoadMyDueListItems } from 'src/app/due-list/state/due-list.actions';
import {
  ClearFilterDetails,
  ResetCurrentFilter,
} from 'src/app/core/state/main-table-filter.actions';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { IAttachment } from 'src/app/shared/attachments/models/attachments.model';
import { DueListRoutes } from 'src/app/core/enums/routes.enum';
import {
  ChangeCurrentSelectedDepartment,
  ChangeCurrentSelectedTenant,
  LoadTenantUserAccess,
  PatchCrewManagementType,
  PatchProfilePicture,
} from './tenant-user-access.actions';

export interface UserSettingsState {
  loaded: boolean;
  userAccess?: IUserAccess;
  availableTenants: ITenantUserAccess[];
  availableDepartments: IDepartmentInfo[];
  currentTenant?: ITenantUserAccess;
  currentDepartment?: IDepartmentInfo;
  privileges: IModulePrivileges[];
  privilegesFlat: string[];
  userRoles: IUserRole[];
}

const USER_SETTINGS_STATE_TOKEN = new StateToken<UserSettingsState>('userSettings');
const LAST_SELECTED_TENANT_ID = 'lastSelectedTenantId';
const LAST_SELECTED_DEPARTMENT_ID = 'lastSelectedDepartmentId';
export const ALL_DEPS_TOKEN = 'all_deps';

@State<UserSettingsState>({
  name: USER_SETTINGS_STATE_TOKEN,
  defaults: {
    loaded: false,
    userAccess: undefined,
    availableTenants: [],
    availableDepartments: [],
    currentTenant: undefined,
    currentDepartment: undefined,
    privileges: [],
    privilegesFlat: [],
    userRoles: [],
  },
})
@Injectable()
export class TenantUserAccessState {
  constructor(
    private service: UserOptionsApiService,
    private zone: NgZone,
    private router: Router,
    private toastService: ToastService,
    private actions: Actions
  ) {
    this.actions
      .pipe(
        ofActionSuccessful(ChangeCurrentSelectedDepartment, ChangeCurrentSelectedTenant),
        tap(() => {
          MainTableFilteringHelperService.clear();
        })
      )
      .subscribe();
  }

  @Action(LoadTenantUserAccess, { cancelUncompleted: true })
  loadTenantUserAccess(ctx: StateContext<UserSettingsState>, { tenantId }: LoadTenantUserAccess) {
    return this.service.getUserAccess(tenantId ?? '').pipe(
      tap((result: IUserAccess) => {
        const state = ctx.getState();

        if (result.tenantInfos.length === 0) {
          ctx.setState({
            ...state,
            loaded: true,
            userAccess: result,
          });
        } else {
          const currentTenant = this.doesUserHasLastSelectedTenantAsAvailable(result.tenantInfos)
            ? (result.tenantInfos.find(
                ({ id }) => id === TenantUserAccessState.getLastSelectedTenantId()
              ) as ITenantUserAccess)
            : result.tenantInfos[0];

          const currentDepartment = this.getCurrentDepartment(currentTenant);
          const rolePrivileges = currentTenant.rolePrivileges.find(
            (role) => role.departmentId === currentDepartment.id
          );
          const privilegesFlat = this.combinePrivileges(
            currentTenant.privileges,
            rolePrivileges?.privileges ?? []
          );
          ctx.setState({
            ...state,
            loaded: true,
            userAccess: result,
            currentTenant,
            availableTenants: result.tenantInfos,
            availableDepartments: currentTenant.departmentInfos,
            currentDepartment,
            privileges: currentTenant.privileges,
            privilegesFlat,
            userRoles: currentTenant.userRoles,
          });

          ctx.dispatch(new LoadDueListCounts());
        }
      })
    );
  }

  @Action(ChangeCurrentSelectedTenant, { cancelUncompleted: true })
  changeCurrentSelectedTenant(
    ctx: StateContext<UserSettingsState>,
    { selectedTenantId }: ChangeCurrentSelectedTenant
  ) {
    ctx.dispatch(new ResetCurrentFilter());
    TenantUserAccessState.clearSelectedTenant();
    TenantUserAccessState.setSelectedTenant(selectedTenantId);
    const { availableTenants } = ctx.getState();
    const newSelectedTenant = availableTenants.find(
      (tenant: ITenantUserAccess) => tenant.id === selectedTenantId
    );
    if (newSelectedTenant) {
      const currentDepartment = this.getCurrentDepartment(newSelectedTenant);
      const rolePrivileges = newSelectedTenant.rolePrivileges.find(
        (role) => role.departmentId === currentDepartment.id
      );
      const privilegesFlat = this.combinePrivileges(
        newSelectedTenant.privileges,
        rolePrivileges?.privileges ?? []
      );
      ctx.setState(
        patch({
          currentTenant: newSelectedTenant,
          availableDepartments: newSelectedTenant.departmentInfos,
          currentDepartment,
          privileges: newSelectedTenant.privileges,
          privilegesFlat,
        })
      );
      this.zone.run(() => {
        ctx.dispatch([new LoadDepartments(), new LoadDueListCounts(), new ClearFilterDetails()]);
        this.toastService.success('tenantChangedSuccessfully');
        this.router.navigate(['/']);
      });
    } else {
      this.toastService.success('somethingWentWrong');
    }
  }

  @Action(ChangeCurrentSelectedDepartment, { cancelUncompleted: true })
  changeCurrentSelectedDepartment(
    ctx: StateContext<UserSettingsState>,
    { selectedDepartmentId, withNavigation, comingFromAllDeps }: ChangeCurrentSelectedDepartment
  ) {
    ctx.dispatch(new ResetCurrentFilter());
    if (selectedDepartmentId !== ALL_DEPS_TOKEN) {
      this.changeToSelectedDepartment(ctx, selectedDepartmentId, comingFromAllDeps);
    } else {
      TenantUserAccessState.clearSelectedDepartment();
      ctx.setState(
        patch({
          currentDepartment: { id: ALL_DEPS_TOKEN } as IDepartmentInfo,
        })
      );
      if (withNavigation) {
        ctx.dispatch([new LoadMyDueListItems(), new ClearFilterDetails()]);
        this.router.navigate([DueListRoutes.Main]);
      }
    }
  }

  @Action(PatchProfilePicture)
  patchProfilePicture(
    ctx: StateContext<UserSettingsState>,
    { profilePictureLink }: PatchProfilePicture
  ) {
    const state = ctx.getState();
    const profilePictureMiniature: IAttachment = {
      id: '',
      name: '',
      categories: [],
      type: '',
      isNew: false,
      fileSize: 0,
      link: profilePictureLink,
    };
    ctx.setState(
      patch({
        userAccess: {
          ...(state.userAccess as IUserAccess),
          profilePictureMiniature: profilePictureLink ? profilePictureMiniature : undefined,
        },
      })
    );
  }

  @Action(PatchCrewManagementType)
  patchCrewManagementType(
    ctx: StateContext<UserSettingsState>,
    { tenantId, departmentId, crewManagementType }: PatchCrewManagementType
  ) {
    const state = ctx.getState();
    const updatedDepartments = state.availableDepartments.map((d) =>
      d.id === departmentId ? { ...d, crewManagementType } : d
    );
    const updatedTenants = state.availableTenants.map((t) =>
      t.id === tenantId ? { ...t, departmentInfos: updatedDepartments } : t
    );
    const currentDepartment =
      departmentId === state.currentDepartment?.id
        ? updatedDepartments.find((d) => d.id === departmentId)
        : state.currentDepartment;
    ctx.setState(
      patch({
        availableDepartments: [...updatedDepartments],
        availableTenants: [...updatedTenants],
        currentDepartment,
      })
    );
  }

  @Selector()
  static tenantLoaded(state: UserSettingsState) {
    return state.loaded;
  }

  @Selector()
  static currentTenant(state: UserSettingsState): ITenantUserAccess {
    return state.currentTenant as ITenantUserAccess;
  }

  @Selector()
  static currentDepartment(state: UserSettingsState): IDepartmentInfo {
    return state.currentDepartment as IDepartmentInfo;
  }

  @Selector()
  static userAccess(state: UserSettingsState) {
    return state.userAccess as IUserAccess;
  }

  @Selector()
  static getPrivileges(state: UserSettingsState) {
    return state.privileges;
  }

  @Selector()
  static isProductOwner(state: UserSettingsState) {
    return state.userAccess?.isProductOwner;
  }

  @Selector()
  static getAllAvailableTenants(state: UserSettingsState): ITenantUserAccess[] {
    return state.availableTenants;
  }

  @Selector()
  static getAllAvailableDepartments(state: UserSettingsState): IDepartmentInfo[] {
    return state.availableDepartments;
  }

  @Selector()
  static currentTenantId(state: UserSettingsState): string {
    return state.currentTenant?.id as string;
  }

  @Selector()
  static currentDepartmentId(state: UserSettingsState): string {
    return state.currentDepartment?.id as string;
  }

  @Selector()
  static getPrivilegesFlat(state: UserSettingsState): string[] {
    return state.privilegesFlat;
  }

  private changeToSelectedDepartment(
    ctx: StateContext<UserSettingsState>,
    selectedDepartmentId: string,
    comingFromAllDeps?: boolean
  ) {
    const { availableDepartments } = ctx.getState();
    const newSelectedDepartment = availableDepartments.find(
      (dep: IDepartmentInfo) => dep.id === selectedDepartmentId
    );
    if (newSelectedDepartment) {
      const currentTenant = ctx.getState().currentTenant as ITenantUserAccess;
      const rolePrivileges = currentTenant.rolePrivileges.find(
        (role) => role.departmentId === newSelectedDepartment.id
      );
      const privilegesFlat = this.combinePrivileges(
        currentTenant.privileges,
        rolePrivileges?.privileges ?? []
      );
      TenantUserAccessState.setSelectedDepartment(selectedDepartmentId);
      ctx.setState(
        patch({
          currentDepartment: newSelectedDepartment,
          privilegesFlat,
        })
      );
      if (!comingFromAllDeps) {
        this.zone.run(() => {
          this.toastService.success('departmentChangedSuccessfully');
          ctx.dispatch([new LoadDueListCounts(), new ClearFilterDetails()]);
          this.router.navigate(['/']);
        });
      }
    } else {
      this.toastService.success('somethingWentWrong');
    }
  }

  private getPrivilegesFlatList(privilegesGrouped: IModulePrivileges[]): string[] {
    let flatPrivileges: string[] = [];
    privilegesGrouped.forEach((groupedPrivilege: IModulePrivileges) => {
      if (groupedPrivilege.privileges?.length > 0) {
        flatPrivileges = flatPrivileges.concat(groupedPrivilege.privileges);
      }
    });
    return flatPrivileges;
  }

  private combinePrivileges(
    privileges: IModulePrivileges[],
    rolePrivileges: IModulePrivileges[]
  ): string[] {
    return Array.from(
      new Set([
        ...this.getPrivilegesFlatList(privileges),
        ...this.getPrivilegesFlatList(rolePrivileges),
      ])
    );
  }

  private getCurrentDepartment(tenant: ITenantUserAccess): IDepartmentInfo {
    return TenantUserAccessState.getLastSelectedDepartmentId()
      ? (tenant.departmentInfos.find(
          ({ id }) => id === TenantUserAccessState.getLastSelectedDepartmentId()
        ) as IDepartmentInfo)
      : (tenant.departmentInfos.find((dep) => dep.isEmploymentDepartment) as IDepartmentInfo) ||
          tenant.departmentInfos[0];
  }

  static getLastSelectedTenantId(): string {
    return localStorage.getItem(LAST_SELECTED_TENANT_ID) as string;
  }

  static getUserRoles(state: UserSettingsState): IUserRole[] {
    return state.userRoles || [];
  }

  static getUserForCurrentlySelectedDepartment(state: any): string[] {
    return (state.userSettings.userRoles || [])
      .filter((role: IUserRole) =>
        role.issuedForDepartments.includes(state.userSettings.currentDepartment?.id || '')
      )
      .map((role: IUserRole) => role.role.id);
  }

  static getLastSelectedDepartmentId(): string {
    return localStorage.getItem(LAST_SELECTED_DEPARTMENT_ID) as string;
  }

  static setSelectedTenant(tenantId: string): void {
    localStorage.setItem(LAST_SELECTED_TENANT_ID, tenantId);
  }

  static setSelectedDepartment(departmentId: string): void {
    localStorage.setItem(LAST_SELECTED_DEPARTMENT_ID, departmentId);
  }

  static clearSelectedDepartment(): void {
    localStorage.removeItem(LAST_SELECTED_DEPARTMENT_ID);
  }

  static clearSelectedTenant(): void {
    localStorage.removeItem(LAST_SELECTED_TENANT_ID);
    localStorage.removeItem(LAST_SELECTED_DEPARTMENT_ID);
  }

  private doesUserHasLastSelectedTenantAsAvailable(tenantInfos: ITenantUserAccess[]): boolean {
    return tenantInfos.some(({ id }) => id === TenantUserAccessState.getLastSelectedTenantId());
  }
}
